/**
 * feature-manifests — agregador de manifests de los registries TS que alimentan
 * el catálogo de features auto-descriptivo (Diseño A/C, puente `pnpm gen:features`).
 *
 * Los registries TS de `@space/shared` (country packs hoy; meeting providers,
 * bloques de aprendizaje y módulos en cuanto los agentes F2-A/F2-B los publiquen)
 * NO se redefinen en Rust: emiten un JSON generado (`feature-manifest.gen.json`)
 * que el server embebe y reconcilia al boot (`core_rust::platform::catalog::
 * include_manifest_file`).
 *
 * EXTENSIBILIDAD (receta para agentes posteriores): añadir una fuente = **1 línea**
 * en `allManifestSources()` (importa el array de manifests que ya expone tu
 * registry, con su `category`). Luego `pnpm gen:features` regenera el JSON; el
 * test de determinismo/frescura falla si olvidas regenerarlo.
 */

import { completionCriterionManifests } from "./academy/completion/registry";
import { countryPackManifests, type FeatureManifest } from "./academy/country/packs";
import { dataAccessManifests } from "./academy/data-access";
import { entitlementManifests } from "./academy/entitlement/registry";
import { freeTierManifests } from "./academy/free/registry";
import { gradingSchemeManifests } from "./academy/grading/registry";
import { assignmentModuleManifest } from "./academy/modules/assignment-plugin";
import { courseModuleManifests } from "./academy/modules/plugins";
import { progressionScopeManifests } from "./academy/progression/registry";
import { verticalManifests } from "./academy/verticals";
import { mediaProviderManifests } from "./live";
import { videoBlockManifest } from "./media";

/** Categorías del catálogo (== `Category` serde snake_case del core Rust). */
export type ManifestCategory =
  | "payment_method"
  | "integration"
  | "module_kind"
  | "meeting_provider"
  | "learning_block"
  | "flag"
  | "admin_tool"
  | "io_entity"
  | "country_pack"
  | "grading_scheme"
  | "vertical";

/** Un grupo de manifests de un mismo registry, con la categoría del catálogo. */
export interface ManifestSource {
  /** Id estable de la fuente (agrupación/diagnóstico de deriva). */
  sourceId: string;
  /** Categoría del catálogo a la que pertenecen estos manifests. */
  category: ManifestCategory;
  /** Manifests que aporta el registry. */
  manifests: FeatureManifest[];
}

/**
 * Todas las fuentes de manifest TS. **Punto de extensión**: cada agente añade
 * aquí UNA entrada importando el export de su registry. Ejemplos (comentados
 * hasta que existan en master):
 *
 *   { sourceId: "meeting_providers", category: "meeting_provider",
 *     manifests: mediaProviderManifests() },              // F2-B
 *   { sourceId: "video_block", category: "learning_block",
 *     manifests: [videoBlockManifest()] },                // F2-B
 *   { sourceId: "course_modules", category: "module_kind",
 *     manifests: courseModuleManifests() },               // F2-A
 */
export function allManifestSources(): ManifestSource[] {
  return [
    {
      sourceId: "country_packs",
      category: "country_pack",
      manifests: countryPackManifests(),
    },
    // F2-B: proveedores de clases en vivo (media.provider.<id>) y bloque de vídeo.
    {
      sourceId: "meeting_providers",
      category: "meeting_provider",
      manifests: mediaProviderManifests(),
    },
    {
      sourceId: "video_block",
      category: "learning_block",
      manifests: [videoBlockManifest()],
    },
    // F2-A/F3-A/F3-B: kinds de módulo (lesson, resource, live_session, assignment).
    {
      sourceId: "course_modules",
      category: "module_kind",
      manifests: [...courseModuleManifests(), assignmentModuleManifest()],
    },
    // F3-B: escalas de calificación (ve-0-20, pct-100, letter, pass-fail).
    {
      sourceId: "grading_schemes",
      category: "grading_scheme",
      manifests: gradingSchemeManifests(),
    },
    // F3-C: el module kind `forum` (academy.module.forum) ya viaja en
    // `courseModuleManifests()` (defaultModuleManifests incluye FORUM).
    // FLAGS: verticales de producto (billing/crm/gamificación/grabaciones) —
    // los interruptores "paraguas" del despliegue progresivo por academia.
    {
      sourceId: "academy_verticals",
      category: "vertical",
      manifests: verticalManifests(),
    },
    // M1: criterios de aprobación auto-descriptivos (academy.completion.<key>).
    {
      sourceId: "completion_criteria",
      category: "flag",
      manifests: completionCriterionManifests(),
    },
    // Migración sync→REST (plan 06): palancas de rollback del camino de lectura
    // del cliente por etapa. No gatean control de acceso — ver el módulo.
    {
      sourceId: "data_access",
      category: "flag",
      manifests: dataAccessManifests(),
    },
    // B1 · titularidad (plan 01): qué pasa si el alumno no ha pagado, en
    // cascada plataforma→org→programa→curso→cohorte→alumno.
    {
      sourceId: "entitlement",
      category: "vertical",
      manifests: entitlementManifests(),
    },
    // B6 · niveles (plan 04): los ORÍGENES de etapa se auto-declaran como
    // features (`academy.progression.scope.<key>`), exactamente igual que los
    // criterios de aprobación. Añadir un origen nuevo no toca este archivo.
    {
      sourceId: "progression_scopes",
      category: "flag",
      manifests: progressionScopeManifests(),
    },
    // B5 · «gratis», talleres y funnel (gap analysis §N): clase muestra y
    // captación automática. Lo gratuito NO tiene flag propia — es la ausencia de
    // obligación de pago que ya modela la titularidad.
    {
      sourceId: "free_tier",
      category: "vertical",
      manifests: freeTierManifests(),
    },
  ];
}

// ── Puente con el core Rust (`FeatureManifestFile` serde, camelCase) ───────────

/** Versión del formato del archivo generado (== `FeatureManifestFile.version`). */
export const MANIFEST_FILE_VERSION = 1;

/** Ruta del JSON generado, relativa a la raíz del repo. */
export const MANIFEST_GEN_RELATIVE_PATH = "packages/shared/src/feature-manifest.gen.json";

/** Implicaciones estructuradas (== `FeatureImplications` serde del core Rust). */
export interface ManifestImplications {
  navHidden?: string[];
  flowsBlocked?: string[];
  dataPolicy: "preserved";
}

/** Descriptor en la forma serde del core Rust (`FeatureDescriptor`, camelCase). */
export interface ManifestDescriptor {
  key: string;
  name: string;
  description: string;
  category: ManifestCategory;
  scope: "platform" | "org" | "course";
  status: "stable" | "beta" | "stub" | "deprecated";
  configSchema?: unknown;
  docsUrl?: string;
  /** Cláusulas de dependencia (FLAGS): `"key"`, `"a|b|c"`, sufijo `?` = blanda. */
  requires?: string[];
  conflicts?: string[];
  gateable?: boolean;
  /**
   * Estado de fábrica sin fila en `feature_flags` ni override. Se OMITE cuando
   * vale `true` (el default implícito) para no ensuciar el JSON generado; solo
   * las features nacidas apagadas lo declaran.
   */
  defaultEnabled?: boolean;
  since?: string;
  implications?: ManifestImplications;
}

export interface ManifestFileSourceEntry {
  sourceId: string;
  descriptors: ManifestDescriptor[];
}

export interface FeatureManifestFile {
  version: number;
  /** Siempre `null`: determinismo (sin marcas de tiempo → salida byte-idéntica). */
  generatedAt: null;
  sources: ManifestFileSourceEntry[];
}

/** El core Rust solo conoce stable|beta|stub|deprecated; `experimental` → `beta`. */
function mapStatus(status: FeatureManifest["status"]): ManifestDescriptor["status"] {
  return status === "experimental" ? "beta" : status;
}

/**
 * Adapta una key de registry a la convención del catálogo `^[a-z_]+\.[a-z0-9_.]+$`
 * (== `core_rust::platform::catalog::key_is_valid`). Algunos registries usan slugs
 * con guion (p.ej. `media.provider.custom_link`); la normalización se mantiene por robustez ante slugs futuros con guion;
 * el puente los normaliza a `_` para que el catálogo embebido valide sin deriva.
 * Determinista e idempotente.
 */
export function normalizeCatalogKey(key: string): string {
  return key.replaceAll(/[^a-z0-9_.]/g, "_");
}

/**
 * Normaliza una CLÁUSULA `requires` (`"a|b?|c"`): cada alternativa se normaliza
 * como key preservando los separadores `|` y el marcador blando `?`.
 */
function normalizeRequireClause(clause: string): string {
  return clause
    .split("|")
    .map((alt) => {
      const soft = alt.endsWith("?");
      const key = normalizeCatalogKey(soft ? alt.slice(0, -1) : alt);
      return soft ? `${key}?` : key;
    })
    .join("|");
}

function toDescriptor(m: FeatureManifest, category: ManifestCategory): ManifestDescriptor {
  // Orden de campos FIJO (determinismo de la serialización).
  const d: ManifestDescriptor = {
    key: normalizeCatalogKey(m.key),
    name: m.name,
    description: m.description,
    category,
    scope: m.scope,
    status: mapStatus(m.status),
  };
  if (m.docsSlug) {
    d.docsUrl = `/help/${m.docsSlug}`;
  }
  if (m.requires && m.requires.length > 0) {
    d.requires = m.requires.map(normalizeRequireClause);
  }
  if (m.conflicts && m.conflicts.length > 0) {
    d.conflicts = m.conflicts.map(normalizeCatalogKey);
  }
  if (m.gateable) {
    d.gateable = true;
  }
  if (m.defaultEnabled === false) {
    d.defaultEnabled = false;
  }
  if (m.since) {
    d.since = m.since;
  }
  if (m.implications) {
    const impl: ManifestImplications = { dataPolicy: "preserved" };
    if (m.implications.navHidden && m.implications.navHidden.length > 0) {
      impl.navHidden = m.implications.navHidden;
    }
    if (m.implications.flowsBlocked && m.implications.flowsBlocked.length > 0) {
      impl.flowsBlocked = m.implications.flowsBlocked;
    }
    d.implications = impl;
  }
  return d;
}

/**
 * Construye el archivo de manifest a partir de todas las fuentes. Determinista:
 * sin timestamps y con los descriptores ordenados por `key` dentro de cada
 * fuente.
 */
function byKey(a: ManifestDescriptor, b: ManifestDescriptor): number {
  // Comparación por code-point (determinista, independiente del locale).
  if (a.key < b.key) {
    return -1;
  }
  if (a.key > b.key) {
    return 1;
  }
  return 0;
}

export function buildFeatureManifestFile(): FeatureManifestFile {
  const sources: ManifestFileSourceEntry[] = allManifestSources().map((src) => ({
    sourceId: src.sourceId,
    descriptors: src.manifests.map((m) => toDescriptor(m, src.category)).toSorted(byKey),
  }));
  return { version: MANIFEST_FILE_VERSION, generatedAt: null, sources };
}

/**
 * Serialización canónica (2 espacios + salto final) usada tanto por el generador
 * (`pnpm gen:features`) como por el test de frescura. El orden de claves es el de
 * inserción de los objetos → estable.
 */
export function serializeFeatureManifestFile(): string {
  return `${JSON.stringify(buildFeatureManifestFile(), null, 2)}\n`;
}
